import { useRef, useCallback } from 'react'

import { type UNSAFE_any } from '@flecto/types'

/**
 * A hook to create a debounced callback
 * @param callback - The function to debounce
 * @param delay - The delay in milliseconds
 * @returns A tuple with the debounced function and a cancel function
 */

export function useDebouncedCallback<T extends (...args: UNSAFE_any[]) => void>(
  callback: T,
  delay: number
): [(...args: Parameters<T>) => void, () => void] {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args)
      }, delay)
    },
    [callback, delay]
  )

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  return [debouncedCallback, cancel]
}
